/**
 * Automatic sync of the task label / entity catalog (Business Goals
 * "Labels in this task" / "Entities in this task", saved as
 * `manually_added_labels` / `manually_added_entities`) to the uploaded data.
 *
 * Once a Data Collection details form has finished extracting the distinct
 * labels of its file, every value the rows will actually carry — the
 * Replace-or-create target when the user mapped a file label, the file label
 * itself otherwise; dropped rows (`mapping === null`) excluded — that the
 * task does not define yet is added to the catalog. The catalog is only
 * added to: a task label no file covers stays, and the "Missing label as
 * defined in task creation" banner with its Fix menu still points at it.
 *
 * Values added this way are remembered (`autoAdded`) so that
 * - a value the user then maps elsewhere or drops is removed again
 *   (otherwise it would turn into a bogus "missing" label), and
 * - a value the user removed by hand (Business Goals, or the minus toggle
 *   in Select Label / Select Entity) is not added back while the mapping
 *   still points at it. Such a value is forgotten once nothing maps to it,
 *   so mapping to it again adds it again.
 *
 * The preview that feeds the forms covers the first 20 MB of a file only;
 * labels that first appear later are merged by the data processor after
 * ingest (`sync_task_catalog_from_file`, always sent for labelled uploads).
 */

const nonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

/**
 * Values the rows of the given details forms will be labelled with, in
 * first-seen order: the mapped value of every extracted label (or the label
 * itself), dropped rows excluded, opted-out forms (`hasLabels === false`)
 * ignored.
 */
export function coveredCatalogValues(detailsList) {
    const covered = [];
    const seen = new Set();
    (detailsList || []).forEach((details) => {
        if (!details || details.hasLabels === false) return;
        const mappings = details.labelMappings || {};
        (details.extractedLabels || []).forEach((label) => {
            const mapped = mappings[label];
            if (mapped === null) return;
            const value = mapped ?? label;
            if (!nonEmptyString(value) || seen.has(value)) return;
            seen.add(value);
            covered.push(value);
        });
    });
    return covered;
}

/**
 * True while any labelled form is still reading its columns or extracting
 * its distinct labels — nothing is judged until the data has settled, so the
 * catalog does not flicker while a column is re-scanned.
 *
 * @param {Array<{details: object, columnsLoading?: boolean}>} entries
 */
export function isCatalogAutoSyncPending(entries) {
    return (entries || []).some(
        ({ details, columnsLoading }) =>
            Boolean(details) &&
            details.hasLabels !== false &&
            Boolean(details.labelsLoading || columnsLoading)
    );
}

/**
 * Next catalog and auto-added history for the current forms.
 *
 * @param {object} params
 * @param {string[]} params.catalog Current task labels (`form.labels`).
 * @param {string[]} params.autoAdded Values previously added by this sync.
 * @param {Array<{details: object, columnsLoading?: boolean}>} params.entries
 *   The details forms currently on screen.
 * @returns {{labels: string[], autoAdded: string[], changed: boolean}}
 *   `labels` is the same array as `catalog` when nothing changed.
 */
export function autoSyncTaskCatalog({ catalog, autoAdded, entries }) {
    const current = Array.isArray(catalog) ? catalog : [];
    const history = Array.isArray(autoAdded) ? autoAdded : [];
    const unchanged = { labels: current, autoAdded: history, changed: false };
    if (!entries?.length || isCatalogAutoSyncPending(entries)) return unchanged;

    const covered = coveredCatalogValues(entries.map((entry) => entry.details));
    const coveredSet = new Set(covered);
    const catalogSet = new Set(current);
    const historySet = new Set(history);

    const additions = covered.filter((value) => !catalogSet.has(value) && !historySet.has(value));
    const removals = history.filter((value) => catalogSet.has(value) && !coveredSet.has(value));
    const removalSet = new Set(removals);
    // Forget values that are neither in the catalog nor mapped to any more;
    // keep the ones the user removed by hand while a file still maps to them.
    const keptHistory = history.filter(
        (value) => !removalSet.has(value) && (catalogSet.has(value) || coveredSet.has(value))
    );
    const nextHistory = [...keptHistory, ...additions];

    const labels =
        additions.length || removals.length
            ? [...current.filter((value) => !removalSet.has(value)), ...additions]
            : current;
    const historyChanged =
        nextHistory.length !== history.length || nextHistory.some((value, index) => value !== history[index]);
    if (labels === current && !historyChanged) return unchanged;
    return { labels, autoAdded: nextHistory, changed: true };
}

/** The auto-added values currently in the catalog — what the info banner lists. */
export function autoAddedInCatalog(autoAdded, catalog) {
    const catalogSet = new Set(catalog || []);
    return (autoAdded || []).filter((value) => catalogSet.has(value));
}

/**
 * Copy of the Data Collection info banner after values were auto-added.
 * `isExistingTask` (Add Data modal) has no Business Goals step to point at.
 */
export function autoAddedBannerText({ values = [], isNer = false, fileCount = 1, isExistingTask = false } = {}) {
    if (!values.length) return '';
    const noun = isNer ? 'Entities' : 'Labels';
    const source = fileCount > 1 ? 'files' : 'file';
    const base = `${noun} added to the task from your ${source}: ${values.join(', ')}.`;
    return isExistingTask ? base : `${base} You can edit them in the Business Goals step.`;
}
